import React from 'react';
import { SearchDropdown } from '../../../../../components';
import { FormInput } from '../../../../../components/shared/inputs';

interface GeneralConfigurationSelectorProps {
  isEnabled: boolean;
  selectedMountSystem?: string;
  selectedRoofConduitSize?: string;
  selectedRoofConduitType?: string;
  selectedBosConduit?: string;
  selectedTrenchConduitSize?: string;
  selectedTrenchConduitType?: string;
  trenchLength?: string;
  trenchDepth?: string;
  onMountSystemSelect: (system: string) => void;
  onRoofConduitSizeSelect: (size: string) => void;
  onRoofConduitTypeSelect: (type: string) => void;
  onBosConduitChange: (value: string) => void;
  onTrenchConduitSizeSelect: (size: string) => void;
  onTrenchConduitTypeSelect: (type: string) => void;
  onTrenchLengthChange: (length: string) => void;
  onTrenchDepthChange: (depth: string) => void;
  mountSystems: { id: string; value: string }[];
  conduitTypes: { id: string; value: string }[];
  conduitSizes: { id: string; value: string }[];
  isLoading: boolean;
  error?: string;
}

export const GeneralConfigurationSelector: React.FC<GeneralConfigurationSelectorProps> = ({
  isEnabled,
  selectedMountSystem,
  selectedRoofConduitSize,
  selectedRoofConduitType,
  selectedBosConduit,
  selectedTrenchConduitSize,
  selectedTrenchConduitType,
  trenchLength,
  trenchDepth,
  onMountSystemSelect,
  onRoofConduitSizeSelect,
  onRoofConduitTypeSelect,
  onBosConduitChange,
  onTrenchConduitSizeSelect,
  onTrenchConduitTypeSelect,
  onTrenchLengthChange,
  onTrenchDepthChange,
  mountSystems,
  conduitTypes,
  conduitSizes,
  isLoading,
  error
}) => {

  const handleSelectOption = (callback: (value: string) => void) => (option: { id: string; label: string } | null) => {
    callback(option?.label || '');
  };

  const mountSystemOptions = mountSystems?.map(system => ({
    id: system.id || '',
    label: system.value || ''
  })) || [];

  const conduitTypeOptions = conduitTypes?.map(type => ({
    id: type.id || '',
    label: type.value || ''
  })) || [];

  const conduitSizeOptions = conduitSizes?.map(size => ({
    id: size.id || '',
    label: size.value || ''
  })) || [];

  if (!isEnabled) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <p className="text-gray-500 text-xs 3xl:text-sm">Select a task to configure general mounting details</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-slate-200/70">
      {/* Mount System */}
      <div className="pb-4 lg:pb-0 lg:pr-5 space-y-3">
        <div className="flex items-center gap-2">
          <svg className="w-3.5 h-3.5 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h4v4H4V6zm6 0h4v4h-4V6zm6 0h4v4h-4V6zM4 14h4v4H4v-4zm6 0h4v4h-4v-4zm6 0h4v4h-4v-4z" />
          </svg>
          <span className="text-[10px] 3xl:text-xs uppercase tracking-[0.15em] font-bold text-slate-600">Mount System</span>
        </div>
        <div>
          <label className="block text-xs 3xl:text-sm font-semibold text-gray-700 mb-1">
            Roof or Ground Mount System
          </label>
          <SearchDropdown
            options={mountSystemOptions}
            onSelect={handleSelectOption(onMountSystemSelect)}
            placeholder="Choose mount system..."
            isLoading={isLoading}
            error={error}
            className="text-sm"
            inputClassName="h-8 text-xs 3xl:text-sm"
            value={selectedMountSystem ? mountSystemOptions.find(opt => opt.label === selectedMountSystem || opt.id === selectedMountSystem) || null : null}
            disabled={!isEnabled}
          />
        </div>
      </div>

      {/* Roof-mount Conduit */}
      <div className="py-4 lg:py-0 lg:px-5 space-y-3">
        <div className="flex items-center gap-2">
          <svg className="w-3.5 h-3.5 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M10 6v6m4 0v6M4 12h16M4 18h16" />
          </svg>
          <span className="text-[10px] 3xl:text-xs uppercase tracking-[0.15em] font-bold text-slate-600">Roof-mount Conduit</span>
        </div>
        <div>
          <label className="block text-xs 3xl:text-sm font-semibold text-gray-700 mb-1">Roof-mount Conduit Size</label>
          <SearchDropdown
            options={conduitSizeOptions}
            onSelect={handleSelectOption(onRoofConduitSizeSelect)}
            placeholder="Choose size..."
            isLoading={isLoading}
            error={error}
            className="text-sm"
            inputClassName="h-8 text-xs 3xl:text-sm"
            value={selectedRoofConduitSize ? conduitSizeOptions.find(opt => opt.label === selectedRoofConduitSize) || null : null}
            disabled={!isEnabled}
          />
        </div>
        <div>
          <label className="block text-xs 3xl:text-sm font-semibold text-gray-700 mb-1">Roof-mount Conduit Type</label>
          <SearchDropdown
            options={conduitTypeOptions}
            onSelect={handleSelectOption(onRoofConduitTypeSelect)}
            placeholder="Choose type..."
            isLoading={isLoading}
            error={error}
            className="text-sm"
            inputClassName="h-8 text-xs 3xl:text-sm"
            value={selectedRoofConduitType ? conduitTypeOptions.find(opt => opt.label === selectedRoofConduitType) || null : null}
            disabled={!isEnabled}
          />
        </div>
        <FormInput
          label="BOS Conduit"
          value={selectedBosConduit || ''}
          onChange={onBosConduitChange}
          placeholder="Enter BOS conduit..."
          disabled={!isEnabled}
          inputClassName="h-8 text-xs 3xl:text-sm focus:ring-blue-200 focus:border-blue-300"
        />
      </div>

      {/* Trenching */}
      <div className="pt-4 lg:pt-0 lg:pl-5 space-y-3">
        <div className="flex items-center gap-2">
          <svg className="w-3.5 h-3.5 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5-2V6l5 2 6-2 5 2v12l-5-2-6 2z" />
          </svg>
          <span className="text-[10px] 3xl:text-xs uppercase tracking-[0.15em] font-bold text-slate-600">Trench Conduit</span>
        </div>
        <div>
          <label className="block text-xs 3xl:text-sm font-semibold text-gray-700 mb-1">Trench Conduit Size</label>
          <SearchDropdown
            options={conduitSizeOptions}
            onSelect={handleSelectOption(onTrenchConduitSizeSelect)}
            placeholder="Choose size..."
            isLoading={isLoading}
            error={error}
            className="text-sm"
            inputClassName="h-8 text-xs 3xl:text-sm"
            value={selectedTrenchConduitSize ? conduitSizeOptions.find(opt => opt.label === selectedTrenchConduitSize) || null : null}
            disabled={!isEnabled}
          />
        </div>
        <div>
          <label className="block text-xs 3xl:text-sm font-semibold text-gray-700 mb-1">Trench Conduit Type</label>
          <SearchDropdown
            options={conduitTypeOptions}
            onSelect={handleSelectOption(onTrenchConduitTypeSelect)}
            placeholder="Choose type..."
            isLoading={isLoading}
            error={error}
            className="text-sm"
            inputClassName="h-8 text-xs 3xl:text-sm"
            value={selectedTrenchConduitType ? conduitTypeOptions.find(opt => opt.label === selectedTrenchConduitType) || null : null}
            disabled={!isEnabled}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormInput
            label="Trench Length"
            value={trenchLength || ''}
            onChange={onTrenchLengthChange}
            placeholder="Enter length..."
            disabled={!isEnabled}
            inputClassName="h-8 text-xs 3xl:text-sm focus:ring-blue-200 focus:border-blue-300"
          />
          <FormInput
            label="Trench Depth"
            value={trenchDepth || ''}
            onChange={onTrenchDepthChange}
            placeholder="Enter depth..."
            disabled={!isEnabled}
            inputClassName="h-8 text-xs 3xl:text-sm focus:ring-blue-200 focus:border-blue-300"
          />
        </div>
      </div>
    </div>
  );
};
